import {
  Avatar,
  Box,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";

export type Section = {
  emoji: string;
  title: string;
  time: string;
  lines: string[];
};

export function parseShorthandBullet(text: string): Section[] {
  if (!text) return [];
  // Regex: emoji(s) **title** (time)\ncontent... (until next emoji ** or end)
  // Use a simpler emoji matcher for broad compatibility
  const sectionRegex =
    /(\S+) \*\*(.*?)\*\* \(([^)]*)\)\n([\s\S]*?)(?=(?:\S+ \*\*|$))/g;
  const sections: Section[] = [];
  let match;
  while ((match = sectionRegex.exec(text)) !== null) {
    const [, emoji, title, time, content] = match;
    const lines = content
      .split(/\n+/)
      .map((l) => l.trim())
      .filter(Boolean);
    sections.push({ emoji, title, time, lines });
  }
  return sections;
}

export default function NotesSectionList({
  shorthandBullet,
  onSeek,
}: {
  shorthandBullet: string;
  onSeek?: (seconds: number) => void;
}) {
  const sections = parseShorthandBullet(shorthandBullet);
  if (!sections.length) return null;

  // Helper to parse start time from section.time (format: 00:56 - 04:38 or 00:56 - 00:56)
  function parseStartTime(time: string): number | null {
    const match = time.match(/(\d{2}):(\d{2})/);
    if (!match) return null;
    const min = parseInt(match[1], 10);
    const sec = parseInt(match[2], 10);
    return min * 60 + sec;
  }

  return (
    <List disablePadding>
      {sections.map((section, idx) => (
        <ListItem alignItems="flex-start" disableGutters key={idx}>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: "transparent", fontSize: 28 }}>
              {section.emoji}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight={700} fontSize={16}>
                  {section.title}
                </Typography>
                <Chip
                  label={section.time}
                  size="small"
                  color="primary"
                  sx={{ ml: 1, cursor: onSeek ? "pointer" : undefined }}
                  onClick={() => {
                    if (onSeek) {
                      const seconds = parseStartTime(section.time);
                      if (seconds !== null) onSeek(seconds);
                    }
                  }}
                />
              </Box>
            }
            secondary={
              <Box component="ul" sx={{ pl: 2, mb: 0, mt: 1 }}>
                {section.lines.map((line, i) => (
                  <li key={i}>
                    <Typography variant="body2" color="text.secondary">
                      {line}
                    </Typography>
                  </li>
                ))}
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );
}
